import { z } from 'zod';

import { ComponentDefinition } from '../../types/ComponentDefinition';
import { ComponentPayloadValidator, ComponentValidator } from '../Component';
import { AbstractPort, Port } from '../fields/AbstractPort';
import { MaximumPowerPointCurrent } from '../fields/MaximumPowerPointCurrent';
import { MaximumPowerPointVoltage } from '../fields/MaximumPowerPointVoltage';
import { OpenCircuitVoltage } from '../fields/OpenCircuitVoltage';
import { PowerTolerance } from '../fields/PowerTolerance';
import { ShortCircuitCurrent } from '../fields/ShortCircuitCurrent';
import { SolarTechnologies } from '../fields/SolarTechnologies';
import { TemperatureCoefficient } from '../fields/TemperatureCoefficient';

const port = AbstractPort.extend({
    DC: Port.DC.extend({
        maximumPowerPointCurrent: MaximumPowerPointCurrent.validator,
        maximumPowerPointVoltage: MaximumPowerPointVoltage.validator,
        openCircuitVoltage: OpenCircuitVoltage.validator,
        powerTolerance: PowerTolerance.validator,
        shortCircuitCurrent: ShortCircuitCurrent.validator,
        temperatureCoefficientIsc: TemperatureCoefficient.validator,
        temperatureCoefficientPmax: TemperatureCoefficient.validator,
        temperatureCoefficientVoc: TemperatureCoefficient.validator,
    }).default({}),
    powerFlowDirection: Port.powerFlowDirection.literals.output,
}).default({});

const SolarBase = z.object({
    type: z.literal('solar').default('solar'),
    electrical: z
        .object({
            ports: z.tuple([port]).default([undefined]),
            solarTechnologies: SolarTechnologies.validator.default([]),
        })
        .default({}),
});

export const SolarValidator = ComponentValidator.merge(SolarBase);

export const SolarPayloadValidator = ComponentPayloadValidator.merge(SolarBase);

export const SolarDefinition: ComponentDefinition = {
    type: 'solar',
    name: 'Solar Panel',
    plural: 'Solar Panels',
    indicator: 'PV',
    hasDynamicNumberOfPorts: false,
    interoperabilityDataModel: true,
    ports: {
        min: 1,
        max: 1,
        powerFlowDirections: ['output'],
        allowedControlMethods: ['constant-power', 'constant-current', 'power-voltage'],
    },
    liveData: {
        enabled: true,
        showGraphs: true,
    },
    validators: {
        component: SolarValidator,
        payload: SolarPayloadValidator,
    },
};

export type Solar = z.infer<typeof SolarValidator>;
export type SolarPayload = z.infer<typeof SolarPayloadValidator>;
